import { GUI } from 'lil-gui';
import { VisualizationParams } from './types';

/**
 * ControlPanel creates a lil-gui instance with sliders and color pickers
 * for real-time adjustment of the neutron star visualization parameters.
 * Each control fires the onChange callback with the full params object.
 */
export class ControlPanel {
  public gui: GUI;
  public params: VisualizationParams;

  constructor(onChange: (params: VisualizationParams) => void) {
    this.params = {
      spinSpeed: 2.9,
      inclinationAngle: 0,
      magneticOffsetAngle: 18,
      starColor: '#264497',
      jetSpeed: 100,
      jetParticleCount: 10000,
      jetAccentColor: '#98f0be',
      orbitalSpeed: 0.06,
      eccentricity: 0.3,
      semiMajorAxis: 9.2,
      fieldLineCount: 6,
      azimuthalPlaneCount: 4,
      fieldLineExtent: 6.0,
      fieldLineColor: '#4fc3f7',
      fieldLineVisible: true,
    };

    this.gui = new GUI({ title: 'Neutron Star Controls' });

    const fireChange = () => {
      onChange({ ...this.params });
    };

    // Spin speed slider: range [0.1, 10], step 0.1, default 1
    this.gui
      .add(this.params, 'spinSpeed', 0.1, 10, 0.1)
      .name('Spin Speed')
      .onChange(fireChange);

    // Inclination angle slider: range [0, 90], step 1, default 0
    this.gui
      .add(this.params, 'inclinationAngle', 0, 90, 1)
      .name('Inclination Angle')
      .onChange(fireChange);

    // Magnetic offset angle slider: range [0, 90], step 1, default 15
    this.gui
      .add(this.params, 'magneticOffsetAngle', 0, 90, 1)
      .name('Magnetic Offset')
      .onChange(fireChange);

    // Star color picker: default #B4C8FF
    this.gui
      .addColor(this.params, 'starColor')
      .name('Star Color')
      .onChange(fireChange);

    // Jet speed slider: range [1, 100], step 1, default 50
    this.gui
      .add(this.params, 'jetSpeed', 1, 100, 1)
      .name('Jet Speed')
      .onChange(fireChange);

    // Jet particle count slider: range [100, 10000], step 100, default 2000
    this.gui
      .add(this.params, 'jetParticleCount', 100, 10000, 100)
      .name('Jet Particles')
      .onChange(fireChange);

    // Jet accent color picker: default #FFA500 (orange)
    this.gui
      .addColor(this.params, 'jetAccentColor')
      .name('Jet Color')
      .onChange(fireChange);

    // Orbit folder
    const orbitFolder = this.gui.addFolder('Orbit');
    orbitFolder.add(this.params, 'orbitalSpeed', 0.01, 2.0, 0.01).name('Orbital Speed').onChange(fireChange);
    orbitFolder.add(this.params, 'eccentricity', 0, 0.99, 0.01).name('Eccentricity').onChange(fireChange);
    orbitFolder.add(this.params, 'semiMajorAxis', 1, 20, 0.1).name('Orbit Size').onChange(fireChange);

    // Field Lines folder
    const fieldLinesFolder = this.gui.addFolder('Field Lines');
    fieldLinesFolder.add(this.params, 'fieldLineCount', 2, 10, 1).name('Line Count').onChange(fireChange);
    fieldLinesFolder.add(this.params, 'azimuthalPlaneCount', 2, 8, 1).name('Azimuthal Planes').onChange(fireChange);
    fieldLinesFolder.add(this.params, 'fieldLineExtent', 2.0, 12.0, 0.5).name('Extent').onChange(fireChange);
    fieldLinesFolder.addColor(this.params, 'fieldLineColor').name('Color').onChange(fireChange);
    fieldLinesFolder.add(this.params, 'fieldLineVisible').name('Visible').onChange(fireChange);

    // Credit line at the bottom of the panel
    const creditDiv = document.createElement('div');
    creditDiv.textContent = 'Created by Andy Kong';
    creditDiv.style.cssText = 'padding: 8px 12px; color: #888; font-size: 11px; text-align: center;';
    this.gui.domElement.appendChild(creditDiv);
  }

  /**
   * Removes the GUI from the DOM and cleans up event listeners.
   */
  dispose(): void {
    this.gui.destroy();
  }
}
